import React, { useRef, useEffect, useState, useMemo, ComponentType, SVGProps } from 'react'
import { Box, Stack, Typography, Fade, Paper, Grow } from '@mui/material'
import { Chart, ChartData, ChartOptions } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import useInitialRender from '../../../hooks/useInitialRender'
import { useTheme } from '@mui/styles'
import { OverflowTooltipWrapper } from '../../OverflowTooltipWrapper/OverflowTooltipWrapper'

import { DonutChartData, DonutLegendItem } from '../types'

interface ChartVisibilityState {
    [key: string]: boolean
}

interface DonutChartProps<T> {
    data: DonutChartData<T>['values']
    legend: DonutLegendItem[]
    hiddenItems: ChartVisibilityState
    title?: string
    size?: number
    disableTotalClick?: boolean
    onRegister?: (chart: Chart<'doughnut'>) => void
    onSliceClick?: (index: number) => void
    onTotalClick?: (visibleData: DonutChartData<T>) => void
    onTitleClick?: (visibleData: DonutChartData<T>) => void
}

const DONUT_CUTOUT = 0.6

export const SingleDonutChart = <T,>({
    data,
    legend,
    hiddenItems,
    title,
    size = 180,
    disableTotalClick,
    onRegister,
    onSliceClick,
    onTotalClick,
    onTitleClick,
}: DonutChartProps<T>) => {
    const initialRender = useInitialRender()

    const theme = useTheme()

    const chartRef = useRef<Chart<'doughnut'> | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [isChartInitialized, setIsChartInitialized] = useState(false)

    const [tooltip, setTooltip] = useState(null)

    useEffect(() => {
        if (chartRef.current && onRegister && !isChartInitialized) {
            onRegister(chartRef.current)
            setIsChartInitialized(true)
        }
    }, [onRegister, isChartInitialized])

    useEffect(() => {
        if (!chartRef.current) return

        legend.forEach((item, index) => {
            const shouldBeHidden = !!hiddenItems[item.label]
            const isVisible = chartRef.current!.getDataVisibility(index)

            if (shouldBeHidden && isVisible) {
                chartRef.current!.toggleDataVisibility(index)
            } else if (!shouldBeHidden && !isVisible) {
                chartRef.current!.toggleDataVisibility(index)
            }
        })

        chartRef.current.update()
    }, [hiddenItems, legend])

    const visibleData = useMemo(() => {
        return data.filter((_, i) => !hiddenItems[legend[i]?.label])
    }, [data, hiddenItems, legend])

    const total = useMemo(() => {
        return visibleData.reduce((sum, val) => sum + val.count, 0)
    }, [visibleData])

    const chartData = useMemo<ChartData<'doughnut'>>(() => {
        const isAllHidden = Object.values(hiddenItems).filter(Boolean).length === legend.length - 1
        const isOnlyOneVisible = isAllHidden || data.map((value) => value.count).filter(Boolean).length === 1

        return {
            labels: legend.map((item) => item.label),
            datasets: [
                {
                    data: data.map((item) => item.count),
                    backgroundColor: legend.map((item) => item.color),
                    borderWidth: isOnlyOneVisible ? 0 : 2,
                    borderColor: theme.palette.figma.grayscale.contentWrapper,
                    hoverBorderWidth: isOnlyOneVisible ? 0 : 3,
                    hoverOffset: isOnlyOneVisible ? 0 : 5,
                    hoverBackgroundColor: legend.map((item) => item.color),
                    hoverBorderColor: theme.palette.figma.grayscale.contentWrapper,
                    selfJoin: true,
                },
            ],
        }
    }, [legend, data, hiddenItems, theme])

    const chartOptions = useMemo<ChartOptions<'doughnut'>>(() => {
        return {
            responsive: true,
            maintainAspectRatio: true,
            cutout: `${DONUT_CUTOUT * 100}%`,
            animation: {
                duration: initialRender ? 0 : 1000,
                easing: 'easeOutQuart',
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                    external: (ctx) => {
                        if (!containerRef.current) return

                        const { tooltip } = ctx

                        if (tooltip.opacity === 0) {
                            setTooltip(null)
                            return
                        }

                        const { label, formattedValue } = tooltip.dataPoints[0]
                        const { backgroundColor } = tooltip.labelColors[0]

                        setTooltip({
                            title: label,
                            value: formattedValue,
                            color: backgroundColor,
                        })
                    },
                },
            },
            onClick: (_, elements) => {
                if (elements.length > 0 && onSliceClick) {
                    onSliceClick(elements[0].index)
                }
            },
        }
    }, [onSliceClick, total, initialRender])

    const isCardClickable = !disableTotalClick && !!total && !!onTitleClick

    return (
        <Box
            onClick={isCardClickable ? () => onTitleClick?.({ title, values: visibleData }) : undefined}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid transparent',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                cursor: isCardClickable ? 'pointer' : 'unset',
                ...(isCardClickable && {
                    '&:hover': {
                        borderColor: 'figma.primary2',
                        bgcolor: 'figma.primary5',
                        '& .donut-card-title': {
                            color: 'figma.primary',
                        },
                    },
                }),
            }}
        >
            <Box
                ref={containerRef}
                onClick={(e) => e.stopPropagation()}
                sx={{
                    position: 'relative',
                    width: size,
                    height: size,
                }}
            >
                <Doughnut
                    data={chartData}
                    options={chartOptions}
                    ref={(ref) => {
                        if (ref) {
                            chartRef.current = ref
                            if (onRegister && !isChartInitialized) {
                                setIsChartInitialized(true)
                            }
                        }
                    }}
                    style={{
                        cursor: !!tooltip && Number(tooltip.value) > 0 ? 'pointer' : 'unset',
                        borderRadius: '50%',
                        border: `1px solid ${theme.palette.figma.grayscale.frameBackground}`,
                    }}
                />

                {!!tooltip && Number(tooltip.value) > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1,
                            pointerEvents: 'none',
                        }}
                    >
                        <Grow in timeout={500} style={{ opacity: 1 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: 'figma.grayscale.contentWrapper',
                                    color: 'figma.grayscale.textHier1',
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: '100%',
                                    width: size * (DONUT_CUTOUT + 0.125),
                                    height: size * (DONUT_CUTOUT + 0.125),
                                }}
                            >
                                <Fade in timeout={600}>
                                    <Stack
                                        direction="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        textAlign="center"
                                        height="100%"
                                        width="100%"
                                    >
                                        <Typography variant="14x700" color={tooltip.color} whiteSpace="wrap">
                                            {tooltip.title}
                                        </Typography>
                                        <Typography
                                            variant="14x500"
                                            whiteSpace="nowrap"
                                        >{`Count: ${tooltip.value} of ${total}`}</Typography>
                                        <Typography variant="14x500" whiteSpace="nowrap">
                                            {`Share: ${total ? Math.round((tooltip.value / total) * 100) : 0}%`}
                                        </Typography>
                                    </Stack>
                                </Fade>
                            </Paper>
                        </Grow>
                    </Box>
                )}

                {title && (
                    <Stack
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        onClick={() => !disableTotalClick && !!total && onTotalClick?.({ title, values: visibleData })}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: `${DONUT_CUTOUT * 100}%`,
                            height: `${DONUT_CUTOUT * 100}%`,
                            borderRadius: '50%',
                            textAlign: 'center',
                            userSelect: 'none',
                            color: 'figma.grayscale.textHier1',
                            ...(!disableTotalClick && !!total && !!onTotalClick
                                ? {
                                      '&:hover': {
                                          color: 'figma.primary',
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                      },
                                  }
                                : {}),
                        }}
                    >
                        <Typography variant="22x600" color="inherit">
                            {total}
                        </Typography>
                    </Stack>
                )}
            </Box>

            {title && (
                <Box mt={1.5} width="100%">
                    <OverflowTooltipWrapper
                        placement="top-start"
                        arrow
                        title={
                            <Stack gap={1} overflow="scroll">
                                <Typography
                                    variant="14x400"
                                    color="figma.grayscale.textHier1"
                                    sx={{
                                        whiteSpace: 'pre-line',
                                        overflow: 'scroll',
                                        maxHeight: '50vh',
                                    }}
                                >
                                    {title}
                                </Typography>
                            </Stack>
                        }
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: 'figma.grayscale.tier7',
                                    px: 2,
                                    py: 1,
                                    borderRadius: '6px',
                                    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                                },
                            },
                            arrow: {
                                sx: {
                                    color: 'figma.grayscale.tier7',
                                },
                            },
                        }}
                    >
                        <Typography
                            className="donut-card-title"
                            variant="16x600"
                            color="figma.grayscale.textHier2"
                            noWrap
                            onClick={(e) => {
                                e.stopPropagation()
                                !disableTotalClick && !!total && onTitleClick?.({ title, values: visibleData })
                            }}
                            sx={{
                                display: 'block',
                                textAlign: 'center',
                                ...(!disableTotalClick && !!total && !!onTitleClick
                                    ? {
                                          cursor: 'pointer',
                                          userSelect: 'none',
                                          '&:hover': {
                                              color: 'figma.primary',
                                          },
                                      }
                                    : {}),
                            }}
                        >
                            {title}
                        </Typography>
                    </OverflowTooltipWrapper>
                </Box>
            )}
        </Box>
    )
}
